import React, { useEffect, useState } from "react";
import { Tab } from "@headlessui/react";
import { User, Settings, ShieldCheck, MapPin, ArrowLeft } from "lucide-react";
import ProfileUserProfile from "./components/ProfileUserProfile.jsx";
import PrivacidadUserProfile from "./components/PrivacidadUserProfile.jsx";
import SeguridadUserProfile from "./components/SeguridadUserProfile.jsx";
import ListasDirecciones from "../../../ListaDirecciones/ListasDirecciones.jsx";
import { FadeIn } from "../../../../../../components/ui/index.js";
import { useEditor } from "../../../../../../App.jsx";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

const UserProfileEditor = ({ setEditorID, setEditor }) => {
    const { stateEditor, setStateEditor } = useEditor();
    // profileTab permite volver desde el editor de dirección directamente a la
    // pestaña de Direcciones; se consume al montar para no quedarse pegado.
    const [selectedIndex, setSelectedIndex] = useState(stateEditor?.profileTab ?? 0);

    useEffect(() => {
        if (stateEditor?.profileTab != null) {
            setStateEditor((prev) => ({ ...prev, profileTab: null }));
        }
    }, []);

    const handleClose = () => {
        setEditor?.(false);
        setStateEditor({
            ...stateEditor,
            editor: false,
            editorType: null,
            editorID: null,
        });
    };

    const tabs = [
        {
            name: "Perfil",
            icon: User,
            component: <ProfileUserProfile setEditorID={setEditorID} setEditor={setEditor} />,
        },
        {
            name: "Privacidad",
            icon: Settings,
            component: <PrivacidadUserProfile />,
        },
        {
            name: "Seguridad",
            icon: ShieldCheck,
            component: <SeguridadUserProfile />,
        },
        {
            name: "Direcciones",
            icon: MapPin,
            component: <ListasDirecciones />,
        },
    ];

    return (
        <Tab.Group
            selectedIndex={selectedIndex}
            onChange={setSelectedIndex}
            className="min-h-full max-h-full flex flex-col w-full lg:w-7/12 mx-auto"
        >
            {/* Header del editor */}
            <div className="glassCard rounded-2xl p-4 mb-3 flex items-center gap-3">
                <button
                    onClick={handleClose}
                    className="btn btn-ghost btn-sm btn-circle flex-shrink-0"
                    title="Volver al perfil"
                    aria-label="Volver al perfil"
                >
                    <ArrowLeft size={18} />
                </button>
                <div className="p-2 bg-primary/20 rounded-lg flex-shrink-0">
                    <User className="h-6 w-6 text-primary" />
                </div>
                <div className="min-w-0">
                    <h1 className="text-xl font-bold text-base-content truncate">Editar perfil de usuario</h1>
                    <p className="text-sm text-base-content/70 truncate">
                        Gestiona tu perfil, privacidad y seguridad
                    </p>
                </div>
            </div>

            {/* Navegación de tabs */}
            <div className="glassCard rounded-2xl flex items-center p-2 mb-3 overflow-x-auto overflow-y-hidden min-h-14 max-h-14">
                <Tab.List className="flex gap-2 p-1 w-max">
                    {tabs.map((tab) => (
                        <Tab
                            key={tab.name}
                            className={({ selected }) =>
                                classNames(
                                    "flex-shrink-0 flex items-center gap-2 rounded-xl py-2 px-4 text-sm font-medium",
                                    "transition-colors duration-200",
                                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                                    selected
                                        ? "bg-primary text-primary-content shadow-md"
                                        : "bg-base-200/60 text-base-content/60 hover:bg-base-200 hover:text-base-content",
                                )
                            }
                        >
                            <tab.icon size={16} className="flex-shrink-0" />
                            <span className="whitespace-nowrap">{tab.name}</span>
                        </Tab>
                    ))}
                </Tab.List>
            </div>

            {/* Contenido de los tabs */}
            <div className="flex-1 overflow-auto w-full">
                <Tab.Panels>
                    {tabs.map((tab, index) => (
                        <Tab.Panel key={index} className="focus:outline-none">
                            <FadeIn distance={12}>{tab.component}</FadeIn>
                        </Tab.Panel>
                    ))}
                </Tab.Panels>
            </div>
        </Tab.Group>
    );
};

export default UserProfileEditor;
